import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Modal } from '@heroui/react';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { pairRoleChip } from '../../operationEditorMeta';
import type { PendingDrop } from '../../operationEditorMeta';

type DropPairDialogProps = {
    pending: PendingDrop | null;
    /** The version a publish would create — `Drop from v4`. */
    nextVersion: number;
    /** The date that version comes into force, as typed in the toolbar. */
    effectiveFrom: string;
    onCancel: () => void;
    onConfirm: () => void;
};

/**
 * Removing a pair always confirms — and on a published template it is not
 * "removing".
 *
 * Two different acts wearing one word. A pair that has only ever lived in an
 * unpublished draft can go with a plain question: nothing posted under it and
 * nothing will. A pair that exists in a **published** version is being *dropped
 * from the next one*, and the sentence has to carry the fact that makes that
 * safe — the entries already posted keep the pair, and only the new version
 * stops posting it.
 *
 * That is why the heading changes wording rather than just its tone, and why the
 * dialog shows the pair it is about to drop: by the time an author has scrolled
 * a six-pair recipe, "pair 3" is a number, not a memory.
 */
export const DropPairDialog: React.FC<DropPairDialogProps> = ({
    pending,
    nextVersion,
    effectiveFrom,
    onCancel,
    onConfirm,
}) => {
    const { t } = useTranslation('pages');
    const chip = pending ? pairRoleChip(pending.role) : null;

    return (
        <Modal
            isOpen={pending !== null}
            onOpenChange={(open) => {
                if (!open) {
                    onCancel();
                }
            }}
        >
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-lg"
                        {...getNodeTestIdProp('operation-editor-drop-dialog')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <div className="flex items-center gap-3">
                                <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-danger-soft">
                                    <Icon
                                        aria-hidden
                                        className="size-5 text-danger-soft-foreground"
                                        icon="gravity-ui:triangle-exclamation"
                                    />
                                </span>
                                <Modal.Heading>
                                    {t(
                                        pending?.isPublished
                                            ? 'operations.editor.drop.titlePublished'
                                            : 'operations.editor.drop.title',
                                        {
                                            number: pending?.displayNumber ?? 0,
                                            next: nextVersion,
                                        },
                                    )}
                                </Modal.Heading>
                            </div>
                        </Modal.Header>
                        <Modal.Body className="flex flex-col gap-4 pb-2">
                            <p
                                className="text-sm text-muted"
                                {...getNodeTestIdProp(
                                    'operation-editor-drop-body',
                                )}
                            >
                                {t(
                                    pending?.isPublished
                                        ? 'operations.editor.drop.bodyPublished'
                                        : 'operations.editor.drop.body',
                                    {
                                        number: pending?.displayNumber ?? 0,
                                        next: nextVersion,
                                        current: pending?.activeVersion ?? 0,
                                        date: effectiveFrom,
                                    },
                                )}
                            </p>

                            {pending && chip ? (
                                <div className="flex flex-col gap-2 rounded-2xl border border-separator bg-surface-secondary p-3">
                                    <span className="text-[9.5px] font-bold uppercase tracking-widest text-muted">
                                        {t('operations.editor.drop.summary')}
                                    </span>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span
                                            className={cn(
                                                'inline-flex w-fit items-center rounded-full px-2.5 py-1',
                                                'text-[10px] font-bold uppercase tracking-widest',
                                                chip.chip,
                                            )}
                                        >
                                            {t(chip.labelKey)}
                                        </span>
                                        <span className="text-sm tabular-nums text-foreground">
                                            {pending.summary}
                                        </span>
                                    </div>
                                </div>
                            ) : null}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                onPress={onCancel}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'operation-editor-drop-cancel',
                                )}
                            >
                                {t('operations.editor.drop.cancel')}
                            </Button>
                            <Button
                                onPress={onConfirm}
                                variant="danger"
                                {...getNodeTestIdProp(
                                    'operation-editor-drop-confirm',
                                )}
                            >
                                {t(
                                    pending?.isPublished
                                        ? 'operations.editor.drop.confirmPublished'
                                        : 'operations.editor.drop.confirm',
                                    { next: nextVersion },
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
